import { apiInstance, gql } from "@/services/httpAdapter";
import { GeoDetailsFragment, LocationOEMsFragment } from "./fragments";
import { errorLogger } from "@Common/helpers/logger";
import type { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { ILocation } from "@/Common/interfaces/location";
import { alphabeticallyBy } from "@/Common/helpers/sorting";
import { IUser } from "@/Common/interfaces/user";

export async function retrieveLocationWithUserPermissions(locationId?: string, userId?: string) {
	const query = gql`
		query getLocationWithUserPermissions($locationId: String!, $userId: String!) {
			getLocation(locationId: $locationId) {
				_id
				locationId
				name
				prefix
				logo
				lastEmployeeIngestionOn
				adjustmentCount
				hasMultipleBranches
				branchesFranchises
				franchises
				timezone
				geoDetails {
					...GeoDetails
				}
				storePictures {
					pictureURL
				}
				oems {
					...LocationOEMs
				}
				branchesOEMs {
					...LocationOEMs
				}
				services {
					type
					accountId
					providerName
				}
				storesDetails {
					isDefaultStore
					name
					email
					geoDetails {
						...GeoDetails
					}
					timezone
				}
			}
			getUser(_id: $userId, locationId: $locationId) {
				_id
				username
				fullName
				position
				locationId
				employeeId
				profilePicture
				actions
				dashboardLayout {
					w
					h
					x
					y
					i
					moved
					static
					isResizable
				}
			}
		}
		${GeoDetailsFragment}
		${LocationOEMsFragment}
	`;

	const variables = { locationId, userId };

	const PROPERTIES = ["_id", "username", "fullName", "position", "locationId", "employeeId", "profilePicture", "actions"] as const;

	try {
		const response: GraphqlQueryResponse<"getLocation", ILocation> &
			GraphqlQueryResponse<"getUser", Pick<IUser, (typeof PROPERTIES)[number]>> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		if (!response.data.data?.getLocation || !response.data.data?.getUser) {
			return null;
		}

		return { user: response.data.data?.getUser, location: response.data.data?.getLocation };
	} catch (err) {
		errorLogger(err as Error, { function: "getLocationWithUserPermissions", message: "Error retrieving location", variables });
		return null;
	}
}

interface ILocationResult extends ILocation {}

export async function retrieveLocation(locationId?: string) {
	const query = gql`
		query getLocation($locationId: String!) {
			getLocation(locationId: $locationId) {
				_id
				locationId
				name
				prefix
				logo
				lastEmployeeIngestionOn
				adjustmentCount
				hasMultipleBranches
				branchesFranchises
				franchises
				timezone
				geoDetails {
					...GeoDetails
				}
				storePictures {
					pictureURL
				}
				oems {
					...LocationOEMs
				}
				branchesOEMs {
					...LocationOEMs
				}
				services {
					type
					accountId
					providerName
				}
				storesDetails {
					isDefaultStore
					name
					email
					geoDetails {
						...GeoDetails
					}
					timezone
				}
			}
		}
		${GeoDetailsFragment}
		${LocationOEMsFragment}
	`;

	const variables = { locationId };

	try {
		const response: GraphqlQueryResponse<"getLocation", ILocationResult> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getLocation;
	} catch (err) {
		errorLogger(err as Error, { function: "retrieveLocation", message: "Error retrieving location", variables });
		return null;
	}
}

const locationListResultFields: (keyof ILocation)[] = ["_id", "locationId", "name", "prefix", "logo"];

export interface ILocationListResult extends Pick<ILocation, (typeof locationListResultFields)[number]> {}

export async function retrieveLocationList(tenantId = "0000100000") {
	const query = gql`
		query getLocations($tenantId: String!) {
			getLocations(tenantId: $tenantId) {
				_id
				locationId
				name
				prefix
				logo
				timezone
				oems {
					franchises {
						franchise
						isCombined
						groups {
							franchiseName
						}
					}
				}
			}
		}
	`;

	const variables = {
		tenantId,
	};

	try {
		const response: GraphqlQueryResponse<"getLocations", ILocationListResult[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getLocations.sort(alphabeticallyBy("name"));
	} catch (err) {
		errorLogger(err as Error, { function: "retrieveLocationList", message: "Error retrieving location list", variables });
		return null;
	}
}

interface IManagedPositionResult {
	managedPositions: string[];
}

export async function retrieveManagedPositions(locationId?: string) {
	const query = gql`
		query getLocation($locationId: String!) {
			getLocation(locationId: $locationId) {
				_id
				locationId
				managedPositions
			}
		}
	`;

	const variables = { locationId };

	try {
		const response: GraphqlQueryResponse<"getLocation", IManagedPositionResult> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getLocation?.managedPositions;
	} catch (err) {
		errorLogger(err as Error, { function: "retrieveManagedPositions", message: "Error retrieving managed positions", variables });
		return null;
	}
}

interface ILocationWithStorePictureResult extends ILocation {}

export async function retrieveLocationWithStorePicture(locationId?: string) {
	const query = gql`
		query getLocationWithStorePicture($locationId: String!) {
			getLocationWithStorePicture(locationId: $locationId) {
				_id
				locationId
				name
				prefix
				logo
				lastEmployeeIngestionOn
				adjustmentCount
				hasMultipleBranches
				branchesFranchises
				franchises
				timezone
				geoDetails {
					...GeoDetails
				}
				storePictures {
					pictureURL
				}
				oems {
					...LocationOEMs
				}
				branchesOEMs {
					...LocationOEMs
				}
				services {
					type
					accountId
					providerName
				}
				storesDetails {
					isDefaultStore
					name
					email
					geoDetails {
						...GeoDetails
					}
					timezone
				}
			}
		}
		${LocationOEMsFragment}
		${GeoDetailsFragment}
	`;

	const variables = { locationId };

	try {
		const response: GraphqlQueryResponse<"getLocationWithStorePicture", ILocationWithStorePictureResult> = await apiInstance.post(
			"/graphql",
			{ query, variables }
		);
		return response.data.data?.getLocationWithStorePicture;
	} catch (err) {
		errorLogger(err as Error, {
			function: "retrieveLocationWithStorePicture",
			message: "Error retrieving location with store picture",
			variables,
		});
		return null;
	}
}

interface IGetLocationFinancialStatementsResult extends Pick<ILocation, "locationId"> {
	oems: (Pick<ILocation["oems"][number], "oemReference" | "version"> & {
		financialStatement: {
			_id: string;
			version: string;
			franchises: {
				franchise: string;
				programs: string[];
			}[];
		};
	})[];
}

export async function getLocationFinancialStatements(locationId?: string) {
	const query = gql`
		query getLocationFinancialStatements($locationId: String!) {
			getLocationFinancialStatements(locationId: $locationId) {
				locationId
				oems {
					oemReference
					version
					financialStatement {
						_id
						version
						franchises {
							franchise
							programs {
								program
								metrics {
									_id
									metricName
									metricKey
								}
							}
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getLocationFinancialStatements", IGetLocationFinancialStatementsResult> =
			await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getLocationFinancialStatements;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getLocationFinancialStatements",
			message: "Error retrieving location with financial statements",
			variables,
		});
		return null;
	}
}
